"use client";
import React from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";
import Image from "next/image";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

export function BillboardAssets({ section }) {
  return (
    <section className="py-12 relative overflow-hidden bg-transparent w-full">
      {/* Massive full-bleed backdrop shape with snug, reduced height */}
      <div className="absolute top-[12%] bottom-[12%] left-0 w-[55%] lg:w-[48%] bg-white/5 rounded-r-[180px] -z-10 border-y border-r border-white/10" />

      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px]">
        <div className="grid lg:grid-cols-12 gap-16 items-center">
          {/* Left Column: Image (Takes up 6 cols) */}
          <motion.div
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 relative flex justify-start items-center py-8"
          >
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/20 bg-neutral-900/80 w-full max-w-[640px] relative">
              <Image src={section.image} width={640} height={450} className="w-full h-auto object-contain" alt="Billboard Map Assets" />
            </div>
          </motion.div>

          {/* Right Column: Text Pushed to the Right Side (5 cols, offset by 1) */}
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5 lg:col-start-8 flex flex-col justify-center lg:pl-6"
          >
            {/* Green Map Icon aligned to top right of block */}
            <div className="flex justify-end mb-6 relative w-20 h-20 self-end">
              <Image
                src={section.mapIcon}
                width={80}
                height={80}
                className="rotate-12 animate-bounce-slow object-contain"
                alt=""
              />
            </div>

            <h2
              className={`text-4xl md:text-5xl lg:text-7xl font-bold text-white leading-tight mb-8 ${caveat.className}`}
              dangerouslySetInnerHTML={{ __html: section.title }}
            />

            <div className="space-y-6">
              {section.paragraphs?.map((p, i) => (
                <p key={i} className="text-gray-300 font-normal leading-relaxed text-xl" dangerouslySetInnerHTML={{ __html: p }} />
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export function BillboardLeads({ section }) {
  return (
    <section className="py-24 relative overflow-hidden bg-transparent w-full">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] space-y-24">
        {/* Title */}
        <h2
          className={`text-4xl md:text-5xl lg:text-7xl font-bold text-white text-center leading-tight ${caveat.className}`}
          dangerouslySetInnerHTML={{ __html: section.title }}
        />

        {/* Row 1: CRM Text + Big Pipeline Image */}
        <div className="grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-5 space-y-8">
            <p className="text-gray-300 font-normal leading-relaxed text-xl" dangerouslySetInnerHTML={{ __html: section.row1Text1 }} />
            <p className="text-gray-300 font-normal leading-relaxed text-xl" dangerouslySetInnerHTML={{ __html: section.row1Text2 }} />
          </div>
          <div className="lg:col-span-7 relative py-4">
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/20 bg-neutral-900/80 relative">
              <Image src={section.image1} width={800} height={550} className="w-full h-auto object-cover" alt="CRM Pipeline" />
            </div>
            {section.arrowIcon && (
              <div className="absolute -bottom-16 -left-12 w-36 h-36 rotate-12 opacity-30 d-none d-lg-block pointer-events-none">
                <Image
                  src={section.arrowIcon}
                  fill
                  className="object-contain"
                  alt=""
                />
              </div>
            )}
          </div>
        </div>

        {/* Row 2: Overlay Image Cards + Quote Text */}
        <div className="grid lg:grid-cols-12 gap-16 items-center pt-8">
          <div className="lg:col-span-6 relative">
            {/* Custom high-fidelity inline SVG starburst to ensure instant load and perfect rendering */}
            <svg
              className="absolute -top-12 -left-12 w-24 h-24 pointer-events-none select-none z-20"
              viewBox="0 0 100 100"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Top vertical curved stroke */}
              <path
                d="M 50 35 C 51 25, 52 15, 55 5"
                stroke="#f97316"
                strokeWidth="5.5"
                strokeLinecap="round"
              />
              {/* Top-Right curved stroke */}
              <path
                d="M 60 42 C 70 34, 80 26, 90 18"
                stroke="#f97316"
                strokeWidth="5.5"
                strokeLinecap="round"
              />
              {/* Left horizontal curved stroke */}
              <path
                d="M 38 48 C 26 46, 14 42, 2 38"
                stroke="#f97316"
                strokeWidth="5.5"
                strokeLinecap="round"
              />
              {/* Bottom-left diagonal curved stroke */}
              <path
                d="M 40 60 C 30 70, 18 80, 6 90"
                stroke="#f97316"
                strokeWidth="5.5"
                strokeLinecap="round"
              />
            </svg>
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/20 bg-neutral-900/80 relative">
              <Image src={section.image2} width={600} height={400} className="w-full h-auto object-cover" alt="Quotation Template" />
            </div>
          </div>
          <div className="lg:col-span-2">
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/20 bg-neutral-900/80 relative">
              <Image src={section.image3} width={200} height={400} className="w-full h-auto object-cover" alt="Quotation Mobile" />
            </div>
          </div>
          <div className="lg:col-span-3 lg:col-start-10 space-y-6">
            <p className="text-gray-300 font-normal leading-relaxed text-xl" dangerouslySetInnerHTML={{ __html: section.row2Text }} />
          </div>
        </div>
      </div>
    </section>
  );
}

export function BillboardCampaigns({ section }) {
  return (
    <section className="py-24 relative overflow-visible bg-transparent w-full">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px]">
        {/* Centered Title with Orange Hand-drawn Underline */}
        <h2
          className={`text-6xl md:text-7xl lg:text-8xl font-bold text-white text-center leading-tight mb-16 ${caveat.className}`}
          dangerouslySetInnerHTML={{ __html: section.title }}
        />

        {/* Two side-by-side text description columns */}
        <div className="grid md:grid-cols-2 gap-16 mb-24 w-full">
          <p className="text-xl text-gray-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: section.text1 }} />
          <p className="text-xl text-gray-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: section.text2 }} />
        </div>

        {/* Side-by-side Image Cards with Absolute Curved Doodles */}
        <div className="relative flex flex-col md:flex-row justify-center items-center gap-24 md:gap-36 pt-16 w-full">
          {/* Centered circular contrast shape peaking behind the two cards (perfectly matches Odoo disk layout) */}
          <div className="absolute top-[50%] left-[55%] -translate-x-1/2 -translate-y-1/2 w-[450px] h-[450px] md:w-[500px] md:h-[500px] bg-white/[0.05] border-2 border-white/[0.08] rounded-full -z-10 pointer-events-none" />

          {/* Left Card: Checklist Mobile */}
          <div className="relative w-full max-w-[340px]">
            {/* Doodle above card */}
            <div className="absolute -top-32 left-1/2 -translate-x-1/2 md:left-[-20px] md:translate-x-0 w-max text-center rotate-[345deg] z-20">
              <span className={`text-white text-xl md:text-2xl font-bold block ${caveat.className} max-w-[220px] leading-tight`}>
                {section.doodle1}
              </span>
              <div className="relative w-20 h-20 mx-auto mt-3">
                <Image
                  src="/odoo/arrows/grey_arrow_md_05.svg"
                  fill
                  className="opacity-30 rotate-140 object-contain"
                  alt=""
                />
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border-[10px] border-white shadow-2xl bg-neutral-900 w-full transition-transform hover:scale-[1.03] duration-300 relative">
              <Image src={section.image1} width={340} height={550} className="w-full h-auto object-cover" alt="Campaigns Mobile" />
            </div>
          </div>

          {/* Right Card: Timesheet Details */}
          <div className="relative w-full max-w-[340px]">
            {/* Doodle above card */}
            <div className="absolute -top-32 left-1/2 -translate-x-1/2 md:right-[-20px] md:translate-x-0 w-max text-center rotate-[15deg] z-20">
              <span className={`text-white text-xl md:text-2xl font-bold block ${caveat.className} max-w-[220px] leading-tight`}>
                {section.doodle2}
              </span>
              <div className="relative w-20 h-20 mx-auto mt-3">
                <Image
                  src="/odoo/arrows/grey_arrow_md_05.svg"
                  fill
                  className="opacity-30 rotate-220 scale-x-[-1] object-contain"
                  alt=""
                />
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border-[10px] border-white shadow-2xl bg-neutral-900 w-full transition-transform hover:scale-[1.03] duration-300 relative">
              <Image src={section.image2} width={340} height={550} className="w-full h-auto object-cover" alt="Campaigns Detail" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function BillboardFinance({ section }) {
  return (
    <section className="py-24 relative overflow-visible bg-transparent w-full">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px]">
        <div className="grid lg:grid-cols-12 gap-16 items-center">
          {/* Left Column: Overlay Accounting Screens with Circular Gray Disk */}
          <div className="lg:col-span-6 relative flex items-center justify-center min-h-[500px] w-full">
            {/* Centered circular contrast shape peaking behind the overlapping screens */}
            <div className="absolute top-[50%] left-[48%] -translate-x-1/2 -translate-y-1/2 w-[340px] h-[340px] md:w-[400px] md:h-[400px] bg-white/[0.05] border-2 border-white/[0.08] rounded-full -z-10 pointer-events-none" />

            {/* Left Screen: Straight accounting preview */}
            <div className="absolute left-[15%] z-10 w-[50%] rounded-2xl overflow-hidden border border-white/20 shadow-2xl bg-neutral-900 transition-transform hover:scale-105 duration-300 relative">
              <Image src={section.image1} width={350} height={450} className="w-full h-auto object-cover" alt="Accounting Draft Bill" />
            </div>

            {/* Right Screen: Rotated accounting preview overlapping foreground */}
            <div className="absolute right-[10%] top-[12%] z-20 w-[45%] rounded-2xl overflow-hidden border border-white/20 shadow-2xl bg-neutral-900 rotate-[10deg] transition-transform hover:scale-105 duration-300 relative">
              <Image src={section.image2} width={300} height={400} className="w-full h-auto object-cover" alt="Accounting Invoice logo" />
            </div>
          </div>

          {/* Right Column: Text Content */}
          <div className="lg:col-span-5 lg:col-start-8 space-y-8 relative pt-6">
            {/* Shooting Star Doodle placed absolutely directly above the S of Simplify! */}
            <div className="absolute -top-16 left-[-12px] z-20">
              <svg
                className="w-28 h-28 rotate-[12deg] pointer-events-none select-none"
                viewBox="0 0 100 100"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                {/* Yellow shooting trails */}
                <path d="M 65 20 C 75 18, 85 24, 90 32" stroke="#fbbf24" strokeWidth="4" strokeLinecap="round" />
                <path d="M 55 10 C 70 8, 85 16, 92 26" stroke="#fbbf24" strokeWidth="4" strokeLinecap="round" />
                {/* 5-point Star outline & yellow fill */}
                <path
                  d="M 40 12 L 48 30 L 68 32 L 52 45 L 58 64 L 40 52 L 22 64 L 28 45 L 12 32 L 32 30 Z"
                  fill="#fbbf24"
                  stroke="#d97706"
                  strokeWidth="4.5"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <h2 className={`text-6xl md:text-7xl lg:text-8xl font-bold text-white leading-tight ${caveat.className}`}>
              {section.title}
            </h2>
            <div className="space-y-6">
              <p className="text-gray-300 font-normal leading-relaxed text-xl" dangerouslySetInnerHTML={{ __html: section.text1 }} />
              <p className="text-gray-300 font-normal leading-relaxed text-xl" dangerouslySetInnerHTML={{ __html: section.text2 }} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
